import { useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';

import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { post } from '../../../redux/api';
import type { NewOIDCProviderValidator, OIDCProvider } from '../../../../shared/model-typings';

import { OIDC_PROVIDERS_QUERY_KEY } from './useOIDCProviders';

export type CreateOIDCProviderRequest = z.infer<typeof NewOIDCProviderValidator>;

/**
 * Hook to create a new OIDC provider.
 * Automatically invalidates the provider list cache and team data on success.
 * Displays success/error toasts automatically.
 */
export default function useCreateOIDCProvider() {
  const queryClient = useQueryClient();

  return useToastedMutation({
    mutationFn: async (request: CreateOIDCProviderRequest) => {
      const { data } = await post<CreateOIDCProviderRequest, OIDCProvider>(
        '/api/oidc/providers',
        request,
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch OIDC providers list
      queryClient.invalidateQueries({ queryKey: [OIDC_PROVIDERS_QUERY_KEY] });
      // Invalidate team queries to update the OIDC provider count badge
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    successToastMessage: 'OIDC provider created successfully',
    errorToastMessage: 'Failed to create OIDC provider',
  });
}
